import i18n from '../i18n/i18n';
import { useState, useEffect } from 'react';

// ── Language → Currency mapping ──
const LANG_CURRENCY = {
  es: 'MXN',
  en: 'USD',
  de: 'EUR',
  fr: 'EUR',
};

// ── Fallback rates (1 MXN → X) — from open.er-api.com April 2026 ──
const FALLBACK_RATES = { MXN: 1, USD: 0.0559, EUR: 0.0485 };

// Try to load cached rates from a previous session
const _loadCached = () => {
  try {
    const raw = localStorage.getItem('currencyRates');
    if (!raw) return null;
    const { rates, ts } = JSON.parse(raw);
    // Accept cached rates if less than 24 hours old
    if (Date.now() - ts < 86400000 && rates?.USD && rates?.EUR) return rates;
  } catch { /* ignore corrupt cache */ }
  return null;
};

let _rates = _loadCached() || { ...FALLBACK_RATES };
let _fetched = !!_loadCached();

// ── Notify listeners when live rates arrive ──
const _listeners = new Set();
export function onRatesReady(fn) { _listeners.add(fn); return () => _listeners.delete(fn); }

// Helper to save rates to localStorage + notify listeners
const _applyRates = (usd, eur) => {
  _rates = { MXN: 1, USD: usd, EUR: eur };
  _fetched = true;
  try {
    localStorage.setItem('currencyRates', JSON.stringify({ rates: _rates, ts: Date.now() }));
  } catch { /* quota exceeded */ }
  _listeners.forEach(fn => fn());
};

// Fetch live rates once (fire-and-forget on import)
// Primary: open.er-api.com (market rates, closer to Google)
// Fallback: frankfurter.app (ECB rates)
(async () => {
  try {
    const res = await fetch('https://open.er-api.com/v6/latest/MXN');
    if (res.ok) {
      const data = await res.json();
      if (data?.rates?.USD && data?.rates?.EUR) {
        _applyRates(data.rates.USD, data.rates.EUR);
        return;
      }
    }
  } catch { /* try fallback */ }
  try {
    const res = await fetch('https://api.frankfurter.app/latest?from=MXN&to=USD,EUR');
    if (res.ok) {
      const data = await res.json();
      if (data?.rates) {
        _applyRates(data.rates.USD, data.rates.EUR);
      }
    }
  } catch { /* keep fallback/cached rates */ }
})();

/** Convert an amount from MXN to the currency for the current language */
export function convertMXN(amountMXN) {
  const lang = (i18n.language || 'es').substring(0, 2);
  const currency = LANG_CURRENCY[lang] || 'MXN';
  const rate = _rates[currency] ?? 1;
  return { value: amountMXN * rate, currency };
}

/** Get the current target currency code */
export function getCurrency() {
  const lang = (i18n.language || 'es').substring(0, 2);
  return LANG_CURRENCY[lang] || 'MXN';
}

// ── Locale map for Intl formatters ──
const LOCALE_MAP = { MXN: 'es-MX', USD: 'en-US', EUR: 'de-DE' };

/**
 * Format an amount (originally in MXN) into the visitor's currency.
 * Returns e.g. "$2,500" for MXN, "$143" for USD, "€133" for EUR.
 */
export function formatPrice(amountMXN) {
  if (amountMXN == null) return '$0';
  const { value, currency } = convertMXN(amountMXN);
  const locale = LOCALE_MAP[currency] || 'es-MX';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.round(value));
}

/**
 * Format with decimals when needed (e.g. tax lines).
 */
export function formatPriceDecimals(amountMXN) {
  if (amountMXN == null) return '$0.00';
  const { value, currency } = convertMXN(amountMXN);
  const locale = LOCALE_MAP[currency] || 'es-MX';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/** Whether we're showing a converted (non-MXN) currency */
export function isConverted() {
  return getCurrency() !== 'MXN';
}

/** Whether we have live (not fallback) rates */
export function hasLiveRates() {
  return _fetched;
}

/**
 * React hook — re-renders the component once when live rates arrive.
 * Call at the top of any component that displays prices.
 */
export function useCurrencyRates() {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (_fetched) return;                       // already have live rates
    return onRatesReady(() => setTick(t => t + 1)); // re-render once
  }, []);
}
